import React, { useEffect } from 'react';
import { ArrowLeft, Instagram as InstagramIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import MetaTags from '../components/SEO/MetaTags';

declare global {
  interface Window {
    instgrm?: {
      Embeds: {
        process(): void;
      };
    };
  }
}

export default function Instagram() {
  // Load Instagram embed script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = '//www.instagram.com/embed.js';
    script.async = true;
    script.onload = () => {
      if (window.instgrm?.Embeds) {
        window.instgrm.Embeds.process();
      }
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup script if component unmounts
      const existingScript = document.querySelector('script[src="//www.instagram.com/embed.js"]');
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, []);

  return (
    <>
      <MetaTags
        title="Instagram - Fownd | Behind the Scenes"
        description="Follow our Instagram for visual stories, team highlights and behind-the-scenes content from Fownd and the Notation team."
        keywords="Fownd Instagram, Notation Instagram, healthcare tech behind the scenes, physical therapy AI team"
        canonical="/instagram"
        ogImage="/fownd.png"
        ogType="website"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#1e3d3b] via-[#2a5a56] to-[#1e3d3b]">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent via-[#1e3d3b]/20 to-[#1e3d3b]/40"></div>
          <div className="relative px-6 py-24 md:py-32 lg:py-40">
            <div className="mx-auto max-w-4xl text-center">
              {/* Back button */}
              <div className="mb-8">
                <Link 
                  to="/blogs" 
                  className="inline-flex items-center text-emerald-400 hover:text-emerald-300 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Blogs & Social
                </Link>
              </div>

              <div className="flex items-center justify-center gap-4 mb-6">
                <InstagramIcon className="w-12 h-12 text-pink-400" />
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight font-productSans">
                  Instagram Posts
                </h1>
              </div>
              
              <p className="text-lg md:text-xl text-slate-200 max-w-3xl mx-auto leading-relaxed">
                Follow our journey behind the scenes at Fownd. See our team in action, product updates, and the story behind Notation.
              </p>
              
              <div className="mt-8">
                <a
                  href="https://www.instagram.com/notation.fownd/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white px-6 py-3 rounded-full font-semibold hover:from-pink-600 hover:to-purple-700 transition-all duration-300 transform hover:scale-105"
                >
                  <InstagramIcon className="w-5 h-5" />
                  Follow on Instagram
                </a>
              </div>
            </div>
          </div>
          <div className="absolute top-20 left-10 w-20 h-20 bg-white/5 rounded-full blur-xl"></div>
          <div className="absolute bottom-20 right-10 w-32 h-32 bg-white/5 rounded-full blur-xl"></div>
        </section>

        {/* Instagram Embed Section */}
        <section className="py-20 px-6">
          <div className="mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Latest from @notation.fownd
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Get an inside look at our team, product development, and the healthcare technology we're building.
              </p>
            </div>

            {/* Instagram Embed Container */}
            <div className="flex justify-center px-2">
              <div className="w-full max-w-none">
                <blockquote 
                  className="instagram-media mx-auto" 
                  data-instgrm-permalink="https://www.instagram.com/notation.fownd/?utm_source=ig_embed&utm_campaign=loading" 
                  data-instgrm-version="14" 
                  style={{ 
                    background: '#FFF', 
                    border: 0, 
                    borderRadius: '3px', 
                    boxShadow: '0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15)', 
                    margin: '1px auto', 
                    maxWidth: '125vw', 
                    minWidth: '500px', 
                    padding: 0, 
                    width: '95%',
                    display: 'block'
                  }}
                >
                  <div style={{ padding: '16px' }}>
                    <a 
                      href="https://www.instagram.com/notation.fownd/?utm_source=ig_embed&utm_campaign=loading" 
                      style={{ 
                        background: '#FFFFFF', 
                        lineHeight: 0, 
                        padding: '0 0', 
                        textAlign: 'center', 
                        textDecoration: 'none', 
                        width: '100%' 
                      }} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <div style={{ 
                          backgroundColor: '#F4F4F4', 
                          borderRadius: '50%', 
                          flexGrow: 0, 
                          height: '40px', 
                          marginRight: '14px', 
                          width: '40px' 
                        }}></div>
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          flexGrow: 1, 
                          justifyContent: 'center' 
                        }}>
                          <div style={{ 
                            backgroundColor: '#F4F4F4', 
                            borderRadius: '4px', 
                            flexGrow: 0, 
                            height: '14px', 
                            marginBottom: '6px', 
                            width: '100px' 
                          }}></div>
                          <div style={{ 
                            backgroundColor: '#F4F4F4', 
                            borderRadius: '4px', 
                            flexGrow: 0, 
                            height: '14px', 
                            width: '60px' 
                          }}></div>
                        </div>
                      </div>
                      <div style={{ padding: '19% 0' }}></div>
                      <div style={{ 
                        display: 'block', 
                        height: '50px', 
                        margin: '0 auto 12px', 
                        width: '50px' 
                      }}>
                        <svg width="50px" height="50px" viewBox="0 0 60 60" version="1.1">
                          <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                            <g transform="translate(-511.000000, -20.000000)" fill="#000000">
                              <g>
                                <path d="M556.869,30.41 C554.814,30.41 553.148,32.076 553.148,34.131 C553.148,36.186 554.814,37.852 556.869,37.852 C558.924,37.852 560.59,36.186 560.59,34.131 C560.59,32.076 558.924,30.41 556.869,30.41 M541,60.657 C535.114,60.657 530.342,55.887 530.342,50 C530.342,44.114 535.114,39.342 541,39.342 C546.887,39.342 551.658,44.114 551.658,50 C551.658,55.887 546.887,60.657 541,60.657 M541,33.886 C532.1,33.886 524.886,41.1 524.886,50 C524.886,58.899 532.1,66.113 541,66.113 C549.9,66.113 557.115,58.899 557.115,50 C557.115,41.1 549.9,33.886 541,33.886 M565.378,62.101 C565.244,65.022 564.756,66.606 564.346,67.663 C563.803,69.06 563.154,70.057 562.106,71.106 C561.058,72.155 560.06,72.803 558.662,73.347 C557.607,73.757 556.021,74.244 553.102,74.378 C549.944,74.521 548.997,74.552 541,74.552 C533.003,74.552 532.056,74.521 528.898,74.378 C525.979,74.244 524.393,73.757 523.338,73.347 C521.94,72.803 520.942,72.155 519.894,71.106 C518.846,70.057 518.197,69.06 517.654,67.663 C517.244,66.606 516.755,65.022 516.623,62.101 C516.479,58.943 516.448,57.996 516.448,50 C516.448,42.003 516.479,41.056 516.623,37.899 C516.755,34.978 517.244,33.391 517.654,32.338 C518.197,30.938 518.846,29.942 519.894,28.894 C520.942,27.846 521.94,27.196 523.338,26.654 C524.393,26.244 525.979,25.756 528.898,25.623 C532.057,25.479 533.004,25.448 541,25.448 C548.997,25.448 549.943,25.479 553.102,25.623 C556.021,25.756 557.607,26.244 558.662,26.654 C560.06,27.196 561.058,27.846 562.106,28.894 C563.154,29.942 563.803,30.938 564.346,32.338 C564.756,33.391 565.244,34.978 565.378,37.899 C565.522,41.056 565.552,42.003 565.552,50 C565.552,57.996 565.522,58.943 565.378,62.101 M570.82,37.631 C570.674,34.438 570.167,32.258 569.425,30.349 C568.659,28.377 567.633,26.702 565.965,25.035 C564.297,23.368 562.623,22.342 560.652,21.575 C558.743,20.834 556.562,20.326 553.369,20.18 C550.169,20.033 549.148,20 541,20 C532.853,20 531.831,20.033 528.631,20.18 C525.438,20.326 523.257,20.834 521.349,21.575 C519.376,22.342 517.703,23.368 516.035,25.035 C514.368,26.702 513.342,28.377 512.574,30.349 C511.834,32.258 511.326,34.438 511.181,37.631 C511.035,40.831 511,41.851 511,50 C511,58.147 511.035,59.17 511.181,62.369 C511.326,65.562 511.834,67.743 512.574,69.651 C513.342,71.625 514.368,73.296 516.035,74.965 C517.703,76.634 519.376,77.658 521.349,78.425 C523.257,79.167 525.438,79.673 528.631,79.82 C531.831,79.965 532.853,80.001 541,80.001 C549.148,80.001 550.169,79.965 553.369,79.82 C556.562,79.673 558.743,79.167 560.652,78.425 C562.623,77.658 564.297,76.634 565.965,74.965 C567.633,73.296 568.659,71.625 569.425,69.651 C570.167,67.743 570.674,65.562 570.82,62.369 C570.966,59.17 571,58.147 571,50 C571,41.851 570.966,40.831 570.82,37.631"></path>
                              </g>
                            </g>
                          </g>
                        </svg>
                      </div>
                      <div style={{ paddingTop: '8px' }}>
                        <div style={{ 
                          color: '#3897f0', 
                          fontFamily: 'Arial,sans-serif', 
                          fontSize: '14px', 
                          fontStyle: 'normal', 
                          fontWeight: 550, 
                          lineHeight: '18px' 
                        }}>
                          View this profile on Instagram
                        </div>
                      </div>
                      <div style={{ padding: '12.5% 0' }}></div>
                    </a>
                    <p style={{ 
                      color: '#c9c8cd', 
                      fontFamily: 'Arial,sans-serif', 
                      fontSize: '14px', 
                      lineHeight: '17px', 
                      marginBottom: 0, 
                      marginTop: '8px', 
                      overflow: 'hidden', 
                      padding: '8px 0 7px', 
                      textAlign: 'center', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap' 
                    }}>
                      <a 
                        href="https://www.instagram.com/notation.fownd/?utm_source=ig_embed&utm_campaign=loading" 
                        style={{ 
                          color: '#c9c8cd', 
                          fontFamily: 'Arial,sans-serif', 
                          fontSize: '14px', 
                          fontStyle: 'normal', 
                          fontWeight: 'normal', 
                          lineHeight: '17px' 
                        }} 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        Fowndinc
                      </a>{' '}
                      (@
                      <a 
                        href="https://www.instagram.com/notation.fownd/?utm_source=ig_embed&utm_campaign=loading" 
                        style={{ 
                          color: '#c9c8cd', 
                          fontFamily: 'Arial,sans-serif', 
                          fontSize: '14px', 
                          fontStyle: 'normal', 
                          fontWeight: 'normal', 
                          lineHeight: '17px' 
                        }} 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        notation.fownd
                      </a>
                      ) • Instagram photos and videos
                    </p>
                  </div>
                </blockquote>
              </div>
            </div>

            {/* Call to Action */}
            <div className="mt-16 text-center">
              <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg p-8 border border-pink-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Stay Connected
                </h3>
                <p className="text-gray-700 mb-6 leading-relaxed max-w-2xl mx-auto">
                  Don't miss out on our latest updates, behind-the-scenes content, and product announcements. Follow us on Instagram for an inside look at the future of healthcare technology.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="https://www.instagram.com/notation.fownd/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-pink-600 hover:to-purple-700 transition-all duration-300 inline-flex items-center justify-center gap-2"
                  >
                    <InstagramIcon className="w-5 h-5" />
                    Follow Us
                  </a>
                  <Link
                    to="/blogs"
                    className="bg-white text-gray-700 px-8 py-3 rounded-lg font-semibold border-2 border-gray-200 hover:bg-gray-50 transition-colors inline-flex items-center justify-center"
                  >
                    Back to Blogs
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
